// ISM evidence links (sql/test-fixes-1.sql ism_evidence_links): a finding or checklist item points at a record of another area.
// Shared by the ISM Audits page, its PDFs and the AI (api/_agent.js link_ism_evidence). No browser APIs here.
// The database fills in label + date from the record and shows a link only to logins that may read that record.

export const LINK_TYPES = [
  { key: 'checklist_run', table: 'checklist_instances', label: 'Checklist run', page: 'checklists' },
  { key: 'crew_certificate', table: 'crew_docs', label: 'Crew certificate', page: 'crew' },
  { key: 'drill', table: 'drill_records', label: 'Drill record', page: 'drill_log' },
  { key: 'document', table: 'documents', label: 'Document', page: 'docs' },
  { key: 'crew_record', table: 'crew', label: 'Crew / training record', page: 'crew' },
  { key: 'crew_log', table: 'crew_log', label: 'Crew log entry', page: 'crew' },
  { key: 'todo', table: 'todo_items', label: 'To Do', page: 'todo' },
]
export const typeOfTable = t => LINK_TYPES.find(x => x.table === t) || null
export const typeOfKey = k => LINK_TYPES.find(x => x.key === k) || null

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export const niceDate = d => { const m = String(d || '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? Number(m[3]) + ' ' + MON[Number(m[2]) - 1] + ' ' + m[1] : '' }

// "Checklist run: Familiarisation — Mia Torres (12 Sep 2026)"
export function linkText(l, withType = true) {
  const t = typeOfTable(l.target_table)
  return (withType && t ? t.label + ': ' : '') + (l.label || 'Record') + (l.target_date ? ' (' + niceDate(l.target_date) + ')' : '')
}

// what the app opens for a link: the page + the "open this record" key that page reads
export function linkOpen(l) {
  const t = typeOfTable(l.target_table); if (!t) return null
  const keys = {}
  if (l.target_table === 'checklist_instances' && l.open_hint) keys.hc_open_checklist = l.open_hint
  if (l.target_table === 'crew_docs') { if (l.open_hint) keys.hc_open_crew_title = l.open_hint; keys.hc_open_crew_tab = 'docs' }
  if (l.target_table === 'documents' && l.open_hint) keys.hc_open_doc = l.open_hint
  if (l.target_table === 'crew' && l.open_hint) keys.hc_open_crew_id = l.open_hint
  return { page: t.page, keys }
}

// how to look records up for the picker / the AI: which columns, the words searched, a label and date like the database's
export const RECORD_SEARCH = {
  checklist_instances: { select: 'id,checklist_id,run_label,started_by,general_notes,started_at,completed_at', order: 'started_at',
    words: (r, c) => [c.checklistTitle && c.checklistTitle[r.checklist_id], r.run_label, r.started_by, r.general_notes],
    label: (r, c) => ((c.checklistTitle && c.checklistTitle[r.checklist_id]) || 'Checklist') + (r.run_label ? ' — ' + r.run_label : ''), date: r => String(r.completed_at || r.started_at || '').slice(0, 10) },
  crew_docs: { select: 'id,title,type,doc_id,issued_date,expiry_date,created_at', order: 'created_at',
    words: r => [r.title, r.type, r.doc_id], label: r => r.title || r.type || 'Certificate', date: r => r.issued_date || String(r.created_at || '').slice(0, 10) },
  drill_records: { select: 'id,drill_type,drill_date,crew_present,notes', order: 'drill_date',
    words: r => [r.drill_type, 'drill', r.crew_present, r.notes], label: r => r.drill_type + ' drill', date: r => r.drill_date },
  documents: { select: 'id,title,document_type,doc_id,issued_date,created_at', order: 'created_at',
    words: r => [r.title, r.document_type, r.doc_id], label: r => r.title || 'Document', date: r => r.issued_date || String(r.created_at || '').slice(0, 10) },
  crew: { select: 'id,name,first_name,last_name,position,contract_start_date', order: 'name',
    words: r => [r.name, r.first_name, r.last_name, r.position], label: r => (r.name || [r.first_name, r.last_name].filter(Boolean).join(' ') || 'Crew member') + (r.position ? ' — ' + r.position : ''), date: r => r.contract_start_date },
  crew_log: { select: 'id,crew_member,action,log_entry,date_from,created_at', order: 'created_at',
    words: r => [r.crew_member, r.action, r.log_entry], label: r => [r.crew_member, r.action || r.log_entry || 'Crew log entry'].filter(Boolean).join(' — '), date: r => r.date_from || String(r.created_at || '').slice(0, 10) },
  todo_items: { select: 'id,list_id,text,notes,assignee,due_date,created_at', order: 'created_at',
    words: r => [r.text, r.notes, r.assignee], label: r => String(r.text || '').slice(0, 200), date: r => r.due_date || String(r.created_at || '').slice(0, 10) },
}

const STOP = new Set(['the', 'of', 'a', 'an', 'to', 'for', 'and', 'with', 'on', 'in', 'link', 'record', 'records', 'run', 'checklist', 'certificate', 'cert', 'document', 'doc', 'drill', 'todo', 'task', 'entry', 'nc', 'finding', 'evidence', 'his', 'her', 'their', 'from', 'this', 'that'])
const norm = s => String(s || '').toLowerCase().replace(/iz(e|ed|ing|ation)\b/g, 'is$1').normalize('NFD').replace(/[̀-ͯ]/g, '')
export const searchWords = q => norm(q).split(/[^a-z0-9]+/).filter(w => w.length > 1 && !STOP.has(w))

// rows of one table -> [{ id, label, date, hits }] best first. Every search word must appear (else the closest half).
export function matchRecords(table, rows, q, ctx = {}) {
  const S = RECORD_SEARCH[table]; if (!S) return []
  const ws = searchWords(q)
  const scored = rows.map(r => {
    const hay = norm(S.words(r, ctx).filter(Boolean).join(' '))
    return { id: r.id, label: S.label(r, ctx), date: S.date(r) || null, hits: ws.filter(w => hay.includes(w)).length, at: String(r[S.order] || '') }
  })
  const full = ws.length ? scored.filter(x => x.hits === ws.length) : scored
  const pool = full.length ? full : scored.filter(x => x.hits > 0 && x.hits >= Math.ceil(ws.length / 2))
  return pool.sort((a, b) => b.hits - a.hits || b.at.localeCompare(a.at))
}

// one line per link for the reports: "Checklist run: Familiarisation — Mia Torres (12 Sep 2026)"
export const linksForPdf = links => (links || []).map(l => linkText(l))
